import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { CreateOrgRequest, OrgOut } from "../api/types";
import { useSession } from "../session";
import { AuthShell } from "../components/AuthShell";
import { Input } from "@grackle/ui";
import { FormNotice, LoadingButton } from "../components/form";

// §2.10: org slug rules — reserved words never resolvable as an org.
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,37}[a-z0-9])?$/;
const RESERVED_SLUGS = new Set(["api", "admin", "app", "www", "dev", "s", "g"]);

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 39);
}

export default function CreateOrg() {
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>();
  const [slugError, setSlugError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  function onNameChange(value: string) {
    setName(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
  }

  function onSlugChange(value: string) {
    setSlugTouched(true);
    setSlug(slugify(value));
  }

  function validate(): boolean {
    let ok = true;

    const trimmedName = name.trim();
    if (trimmedName.length < 1 || trimmedName.length > 200) {
      setNameError("Enter an organization name");
      ok = false;
    } else {
      setNameError(undefined);
    }

    if (!SLUG_RE.test(slug)) {
      setSlugError("Lowercase letters, numbers and dashes only, 2–39 characters");
      ok = false;
    } else if (RESERVED_SLUGS.has(slug)) {
      setSlugError("That address is reserved — try another");
      ok = false;
    } else {
      setSlugError(undefined);
    }

    return ok;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(undefined);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const body: CreateOrgRequest = { name: name.trim(), slug };
      const org = await api<OrgOut>("POST", "/orgs", body);
      await refresh();
      navigate(`/o/${org.slug}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiFail) {
        if (err.code === "conflict") {
          setSlugError("That address is already taken");
        } else if (err.code === "email_unverified") {
          setFormError("Verify your email before creating an organization.");
        } else {
          setFormError(err.message);
        }
      } else {
        setFormError("Couldn't reach Grackle. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Create your organization"
      subtitle="This is where your recordings, teams and members live."
    >
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <Input
          label="Organization name"
          type="text"
          placeholder="Acme Design Co"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          error={nameError}
          help="Shown to everyone you share with"
        />
        <Input
          label="Grackle address"
          type="text"
          variant="mono"
          placeholder="acme-design"
          value={slug}
          onChange={(e) => onSlugChange(e.target.value)}
          error={slugError}
          help={slugError ? undefined : `grackle.app/${slug || "your-address"}`}
        />
        {formError ? <FormNotice tone="error">{formError}</FormNotice> : null}
        <LoadingButton type="submit" block loading={submitting}>
          {submitting ? "Creating…" : "Create organization"}
        </LoadingButton>
      </form>
    </AuthShell>
  );
}
